import classnames from "classnames";
import React, { useEffect, useState } from "react";

import { BreakpointProvider } from "@utils/breakpoints/breakpoints.provider";
import { getDamPath } from "@utils/configuration/aem";
import { ProductSliderSources } from "@utils/enums";
import { useIsClientSide } from "@utils/hooks/isClient.hook";

import { ParamsAlgolia } from "@services/algolia/types/paramsAlgolia.types";
import {
    FeatureToggleEvent,
    FeatureToggleEventName,
} from "@services/featureToggles.services";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { ProductFacingContainer } from "@components/Ecorner/ProductFacing/productFacing.container";
import EnergyLabelComponent from "@components/EnergyLabel/energyLabel.component";
import { ECommerceContainer } from "@components/Product/ECommerce/eCommerce.container";
import {
    EDLPImage,
    TooltipType,
} from "@components/Product/ECommerce/eCommerce.types";
import { ImageViewerContainer } from "@components/Product/ImageViewer/imageViewer.container";
import { ProductBundles } from "@components/Product/ProductBundles/productBundles.container";
import {
    ProductSliderContainer,
    ProductSliderContainerProps,
} from "@components/Product/ProductSlider/productSlider.container";

import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";

import { EcommerceProductAdapter } from "./adapters/ecommerceProduct.adapter";
import { EnergyLabelAdapter } from "./adapters/energyLabel.adapter";
import { ImageViewerAdapter } from "./adapters/imageViewer.adapter";
import { BrandLogo } from "./brandLogo/brandLogo.component";
import { InfoCard } from "./infoCard/infoCard.component";
import { KeyFeatures } from "./keyFeatures/keyFeatures.component";
import { ProductInfo } from "./productInfo/productInfo.component";
import { ProductPageConfig } from "./productPage.config";
import "./productPage.scss";
import { Promotions } from "./promotions/promotions.component";
import { RelatedProductsSlider } from "./relatedProductsSlider/relatedProductsSlider.container";

export type ProductPageComponentProps = {
    product: FullProductApiModel;
    tooltips: {
        homeDelivery: TooltipType;
        pickupInStore: TooltipType;
        serviceProduct: TooltipType;
    };
    infocardLinks: {
        ecoCheque?: string;
        transport?: string;
        webOnly?: string;
        homeDelivery?: string;
    };
    infoLink?: string;
    cashbackPagePath: string;
    edlpimage: EDLPImage;
    contextAwareXfPath: string;
    brandPagePath?: string;
    defaultVideoThumbnailPath?: string;
    sliders?: Partial<
        Record<ProductSliderSources, ProductSliderContainerProps>
    >;
    promoBannerHTML?: string;
    paramsAlgolia: ParamsAlgolia;
    maxVisibleFeatures: number;
    reviewsEnabled: boolean;
};

const ProductPageComponent = ({
    cashbackPagePath,
    contextAwareXfPath,
    edlpimage,
    product,
    tooltips,
    brandPagePath,
    defaultVideoThumbnailPath,
    infoLink,
    sliders,
    promoBannerHTML,
    paramsAlgolia,
    maxVisibleFeatures,
    infocardLinks,
    reviewsEnabled,
}: ProductPageComponentProps) => {
    const { t } = useGranite();
    const [isSticky, setIsSticky] = useState(false);

    const isClientSide = useIsClientSide();

    useEffect(() => {
        window.addEventListener(FeatureToggleEventName, toggleSticky);

        return () => {
            window.removeEventListener(FeatureToggleEventName, toggleSticky);
        };
    }, []);

    useEffect(() => {
        window.hydrator.rehydrateSSRComponent("CALL_TO_ACTION");
    }, [isClientSide]);

    const toggleSticky = (e: FeatureToggleEvent) => {
        setIsSticky(e.isEnabled);
    };

    const { images, videos, usesDefaultImage } = ImageViewerAdapter(product);

    const energyLabel = EnergyLabelAdapter(product);

    const ecommerceProduct = EcommerceProductAdapter(
        product,
        cashbackPagePath,
        contextAwareXfPath,
        edlpimage,
        t,
    );

    return (
        <>
            <div className="grid-container grid-container__pdp">
                <div className="grid-item grid-item__product-image">
                    <ImageViewerContainer
                        images={images}
                        videos={videos}
                        ecoChequesAccepted={ecommerceProduct.ecochequesAccepted}
                        usesdefaultimage={usesDefaultImage}
                        defaultVideoThumbnailPath={defaultVideoThumbnailPath}
                    />
                </div>

                <div className="grid-item grid-item_product-compact d-none d-md-block">
                    <Promotions
                        promotions={ecommerceProduct.promotions}
                        className="d-none d-md-block"
                    />

                    <div className="d-flex align-items-center justify-content-between">
                        {product.brand && (
                            <div className="product-info-compact__logo">
                                <BrandLogo
                                    brand={product.brand}
                                    brandPagePath={brandPagePath}
                                />
                            </div>
                        )}
                        <div className="product-info-compact__energy-label">
                            {energyLabel && energyLabel.energyLabel && (
                                <EnergyLabelComponent
                                    energyLabel={energyLabel.energyLabel}
                                    energyLabelAltText={
                                        energyLabel.energyLabelAltText
                                    }
                                    energyLabelLarge={
                                        energyLabel.energyLabelLarge
                                    }
                                    energyLabelSize={"sm"}
                                    oneByTwo={false}
                                />
                            )}
                        </div>
                    </div>

                    <div className="d-none d-lg-block">
                        <KeyFeatures features={product.features} />
                    </div>

                    {promoBannerHTML && isClientSide && (
                        <div
                            className="product-info-compact__promo-banner"
                            dangerouslySetInnerHTML={{
                                __html: promoBannerHTML,
                            }}
                        ></div>
                    )}
                </div>

                <div
                    className={classnames("grid-item grid-item__ecommerce", {
                        sticky: isSticky,
                    })}
                >
                    <ProductFacingContainer sku={product.sku} />
                    <Promotions
                        promotions={ecommerceProduct.promotions}
                        className="d-block d-md-none"
                    />
                    <ECommerceContainer
                        ecommerceProduct={ecommerceProduct}
                        infoLink={infoLink || ""}
                        tooltips={tooltips}
                    />

                    {promoBannerHTML && isClientSide && (
                        <div
                            className="product-info-compact__promo-banner d-block d-md-none"
                            dangerouslySetInnerHTML={{
                                __html: promoBannerHTML,
                            }}
                        ></div>
                    )}

                    <div className="d-none d-md-block">
                        <ProductBundles product={product} />

                        {ecommerceProduct.ecochequesAccepted && (
                            <div className="info-card__eco-cheques">
                                <InfoCard
                                    title={t("infoCard.ecoCheques.title")}
                                    icon={`${getDamPath()}/product-detail-page/default/eco-cheques.gif`}
                                    iconAltText={t(
                                        "infoCard.ecoCheques.iconAltText",
                                    )}
                                    description={t(
                                        "infoCard.ecoCheques.description",
                                    )}
                                    link={infocardLinks.ecoCheque}
                                    linkLabel={t(
                                        "infoCard.ecoCheques.linkLabel",
                                    )}
                                />
                            </div>
                        )}
                        {ecommerceProduct.clickAndCollectAvailable && (
                            <div className="info-card__transport">
                                <InfoCard
                                    title={t("infoCard.transport.title")}
                                    icon={`${getDamPath()}/product-detail-page/default/transport.png`}
                                    iconAltText={t(
                                        "infoCard.transport.iconAltText",
                                    )}
                                    description={t(
                                        "infoCard.transport.description",
                                    )}
                                    link={infocardLinks.transport}
                                    linkLabel={t(
                                        "infoCard.transport.linkLabel",
                                    )}
                                />
                            </div>
                        )}
                        {ecommerceProduct.isExclusivelyOnline && (
                            <div className="info-card__home-delivery">
                                <InfoCard
                                    title={t(
                                        "infoCard.exclusivelyOnline.title",
                                    )}
                                    icon={`${getDamPath()}/product-detail-page/default/home-delivery.png`}
                                    iconAltText={t(
                                        "infoCard.exclusivelyOnline.iconAltText",
                                    )}
                                    description={t(
                                        "infoCard.exclusivelyOnline.description",
                                    )}
                                    link={infocardLinks.homeDelivery}
                                    linkLabel={t(
                                        "infoCard.exclusivelyOnline.linkLabel",
                                    )}
                                />
                            </div>
                        )}
                        {ecommerceProduct.webOnly && (
                            <div className="info-card__web-only">
                                <InfoCard
                                    title={t("infoCard.webOnly.title")}
                                    icon={`${getDamPath()}/product-detail-page/default/web-only.png`}
                                    iconAltText={t(
                                        "infoCard.webOnly.iconAltText",
                                    )}
                                    description={t(
                                        "infoCard.webOnly.description",
                                    )}
                                    link={infocardLinks.webOnly}
                                    linkLabel={t("infoCard.webOnly.linkLabel")}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid-item grid-item__product-info">
                    <ProductInfo
                        product={product}
                        maxVisibleFeatures={maxVisibleFeatures}
                        reviewsEnabled={reviewsEnabled}
                    />
                </div>

                <div className="grid-item grid-item__relatedProducts">
                    {(sliders?.accessories || sliders?.mainProducts) && (
                        <BreakpointProvider>
                            {sliders.accessories && (
                                <RelatedProductsSlider
                                    productSource={
                                        ProductSliderSources.accessory
                                    }
                                    prefetchedData={
                                        sliders?.accessories.prefetchedData
                                    }
                                />
                            )}
                            {sliders.mainProducts && (
                                <RelatedProductsSlider
                                    productSource={
                                        ProductSliderSources.mainProduct
                                    }
                                    prefetchedData={
                                        sliders?.mainProducts.prefetchedData
                                    }
                                />
                            )}
                        </BreakpointProvider>
                    )}
                </div>

                <div className="grid-item grid-item__info d-block d-md-none">
                    <ProductBundles product={product} />

                    {ecommerceProduct.ecochequesAccepted && (
                        <div className="info-card__eco-cheques">
                            <InfoCard
                                title={t("infoCard.ecoCheques.title")}
                                icon={`${getDamPath()}/product-detail-page/default/eco-cheques.gif`}
                                iconAltText={t(
                                    "infoCard.ecoCheques.iconAltText",
                                )}
                                description={t(
                                    "infoCard.ecoCheques.description",
                                )}
                                link={infocardLinks.ecoCheque}
                                linkLabel={t("infoCard.ecoCheques.linkLabel")}
                            />
                        </div>
                    )}
                    {ecommerceProduct.clickAndCollectAvailable && (
                        <div className="info-card__transport">
                            <InfoCard
                                title={t("infoCard.transport.title")}
                                icon={`${getDamPath()}/product-detail-page/default/transport.png`}
                                iconAltText={t(
                                    "infoCard.transport.iconAltText",
                                )}
                                description={t(
                                    "infoCard.transport.description",
                                )}
                                link={infocardLinks.transport}
                                linkLabel={t("infoCard.transport.linkLabel")}
                            />
                        </div>
                    )}
                    {ecommerceProduct.isExclusivelyOnline && (
                        <div className="info-card__home-delivery">
                            <InfoCard
                                title={t("infoCard.exclusivelyOnline.title")}
                                icon={`${getDamPath()}/product-detail-page/default/home-delivery.png`}
                                iconAltText={t(
                                    "infoCard.exclusivelyOnline.iconAltText",
                                )}
                                description={t(
                                    "infoCard.exclusivelyOnline.description",
                                )}
                                link={infocardLinks.homeDelivery}
                                linkLabel={t(
                                    "infoCard.exclusivelyOnline.linkLabel",
                                )}
                            />
                        </div>
                    )}
                    {ecommerceProduct.webOnly && (
                        <div className="info-card__web-only">
                            <InfoCard
                                title={t("infoCard.webOnly.title")}
                                icon={`${getDamPath()}/product-detail-page/default/web-only.png`}
                                iconAltText={t("infoCard.webOnly.iconAltText")}
                                description={t("infoCard.webOnly.description")}
                                link={infocardLinks.webOnly}
                                linkLabel={t("infoCard.webOnly.linkLabel")}
                            />
                        </div>
                    )}
                </div>

                <div className="grid-item grid-item__frequently-bought-together">
                    {sliders?.["algolia-recommend"] && (
                        <ProductSliderContainer
                            hiddenBreakpoints={[]}
                            prefetch={true}
                            prefetchDataIdentifier={""}
                            prefetchedData={
                                sliders?.["algolia-recommend"].prefetchedData
                            }
                            productSource={
                                ProductSliderSources.algoliaRecommend
                            }
                            amountOfFeatures={0}
                            useInsidePadding={false}
                            url={"1"}
                            showButton={false}
                            showCategoryLink={false}
                            allowInstantAddToCart={false}
                            title={t("sliders.frequentlyBoughtTogether.title")}
                            breakpointsLayoutSettings={JSON.stringify(
                                ProductPageConfig.sliders.recommend,
                            )}
                            lazyLoad={false}
                            paramsAlgolia={paramsAlgolia}
                        />
                    )}
                </div>
            </div>

            <div className="recently-viewed">
                <ProductSliderContainer
                    productSource={ProductSliderSources.recent}
                    amountOfFeatures={0}
                    useInsidePadding={false}
                    showButton={false}
                    showCategoryLink={false}
                    allowInstantAddToCart={true}
                    title={t("sliders.recents.title")}
                    breakpointsLayoutSettings={JSON.stringify(
                        ProductPageConfig.sliders.recent,
                    )}
                    lazyLoad={true}
                    productSku={product.sku}
                />
            </div>
        </>
    );
};

export { ProductPageComponent };
